// /client/src/components/PublicOptIn.js
import React, { useState, useEffect } from 'react';

const PublicOptIn = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    participationMethod: '',
    email: '',
    phone: '',
    school: ''
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [companyName, setCompanyName] = useState('Next Move');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.participationMethod) {
      newErrors.participationMethod = 'Please select how you want to participate';
    }
    
    // Email validation if email method selected
    if (formData.participationMethod === 'email') {
      if (!formData.email.trim()) {
        newErrors.email = 'Email address is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }
    
    // Phone validation if SMS method selected
    if (formData.participationMethod === 'sms') {
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else {
        const cleaned = formData.phone.replace(/\D/g, '');
        if (cleaned.length < 10) {
          newErrors.phone = 'Phone number must be at least 10 digits';
        } else if (cleaned.length > 15) {
          newErrors.phone = 'Phone number is too long';
        }
      }
    }
    
    if (!formData.school) {
      newErrors.school = 'School selection is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch('/api/optin/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const responseData = await response.json();
      
      if (response.ok) {
        setSubmitted(true);
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          participationMethod: '',
          email: '',
          phone: '',
          school: 'Saint Mary\'s College of California'
        });
      } else {
        console.error('Failed to submit opt-in:', responseData);
        setErrors({ 
          submit: responseData.details || responseData.error || 'Failed to submit. Please try again.' 
        });
      }
    } catch (error) {
      console.error('Error submitting opt-in:', error);
      setErrors({ 
        submit: 'Network error. Please check your connection and try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch company name
  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        
        const response = await fetch('/api/company/public');
        if (response.ok) {
          const data = await response.json();
          
          if (data.company?.name) {
            setCompanyName(data.company.name);
            
          }
        } else {
          
        }
      } catch (error) {
        console.error('PublicOptIn: Failed to fetch company name:', error);
      }
    };
    fetchCompanyName();
  }, []);

  // Success message view
  if (submitted) {
    return (
      <section className="hero is-fullheight is-light">
        <div className="hero-body">
          <div className="container">
            <div className="columns is-centered">
              <div className="column is-half">
                <div className="m-6 box has-text-centered">
                  <h1 className="title is-3 mt-6">Thank You!</h1>
                  <p className="subtitle is-5 my-4">
                    Your opt-in request has been received.
                    {formData.participationMethod === 'email' 
                      ? ' Check your inbox and click the confirmation link to finish joining the program.'
                      : ' Check your text messages and reply JOIN to start receiving your daily messages.'}
                  </p>
                  <p className='mb-6'>View our <a href="https://www.nextmovenetwork.com/privacy-policy" target='_blank' className='colorBlue'>Privacy Policy</a> and <a href="https://www.nextmovenetwork.com/terms-of-service" target='_blank' className='colorBlue'>Terms of Use</a>.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Form view
  return (
    <section className="hero is-fullheight is-light">
      <div className="hero-body">
        <div className="container">
          <div className="columns is-centered">
            <div className="column is-two-thirds">
              <div className="box">
                <h1 className="title is-2 has-text-centered mb-5">
                  Join Navigating Next
                </h1>
                
                <div className="content mb-5">
                  <p className="is-size-5">
                    Welcome! Navigating Next is a program designed to support young people through 
                    intentional, small-group conversations that foster clarity, connection, and momentum.
                  </p>
                  <p>
                    By opting in, you'll receive daily prompts and opportunities to engage with your 
                    peers in meaningful reflection and shared experiences. Choose how you'd like to 
                    participate - via email or text message - and we'll keep you connected throughout 
                    your journey.
                  </p>
                  <p className="">
                    By clicking “Sign Up,” you agree to receive automated messages from {companyName}. You also agree to our <a href="https://www.nextmovenetwork.com/privacy-policy" target='_blank' className='colorBlue'>Privacy Policy</a> and <a href="https://www.nextmovenetwork.com/terms-of-service" target='_blank' className='colorBlue'>Terms of Use</a>. Consent is not required as a condition of participation.
                  </p>
                </div>

                <form onSubmit={handleSubmit}>
                  
                  {/* General Error Message */}
                  {errors.submit && (
                    <div className="notification is-danger">
                      <button 
                        className="delete" 
                        onClick={() => setErrors(prev => ({ ...prev, submit: '' }))}
                      ></button>
                      {errors.submit}
                    </div>
                  )}

                  {/* First Name */}
                  <div className="field">
                    <label className="label">First Name *</label>
                    <div className="control">
                      <input 
                        className={`input ${errors.firstName ? 'is-danger' : ''}`}
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="Enter your first name"
                      />
                    </div>
                    {errors.firstName && (
                      <p className="help is-danger">{errors.firstName}</p>
                    )}
                  </div>

                  {/* Last Name */}
                  <div className="field">
                    <label className="label">Last Name *</label>
                    <div className="control">
                      <input 
                        className={`input ${errors.lastName ? 'is-danger' : ''}`}
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Enter your last name"
                      />
                    </div>
                    {errors.lastName && (
                      <p className="help is-danger">{errors.lastName}</p>
                    )}
                  </div>

                  {/* Participation Method */}
                  <div className="field">
                    <label className="label">How would you like to participate? *</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select 
                          name="participationMethod"
                          value={formData.participationMethod}
                          onChange={handleInputChange}
                          className={errors.participationMethod ? 'is-danger' : ''}
                        >
                          <option value="">-- Select an option --</option>
                          <option value="email">Email Message</option>
                          <option value="sms">Text Message (SMS)</option>
                        </select>
                      </div>
                    </div>
                    {errors.participationMethod && (
                      <p className="help is-danger">{errors.participationMethod}</p>
                    )}
                  </div>

                  {/* Email Field - Show only if email method selected */}
                  {formData.participationMethod === 'email' && (
                    <div className="field">
                      <label className="label">Email Address *</label>
                      <div className="control has-icons-left">
                        <input 
                          className={`input ${errors.email ? 'is-danger' : ''}`}
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="Enter your email address"
                        />
                        <span className="icon is-small is-left">
                          <i className="fas fa-envelope"></i>
                        </span>
                      </div>
                      {errors.email && (
                        <p className="help is-danger">{errors.email}</p>
                      )}
                    </div>
                  )}

                  {/* Phone Field - Show only if SMS method selected */}
                  {formData.participationMethod === 'sms' && (
                    <div className="field">
                      <label className="label">Phone Number *</label>
                      <div className="control">
                        <input 
                          className={`input ${errors.phone ? 'is-danger' : ''}`}
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="Enter your phone number"
                        />
                      </div>
                      {errors.phone && (
                        <p className="help is-danger">{errors.phone}</p>
                      )}
                      <p className="small ml-3 mt-1"><span className='is-bold'>Format:</span> 510-234-5678</p>
                    </div>
                  )}

                  {/* School Selection */}
                  <div className="field">
                    <label className="label">School *</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select 
                          name="school"
                          value={formData.school}
                          onChange={handleInputChange}
                        >
                          <option value="">-- Select a school --</option>
                          <option value="Saint Mary's College of California">
                            Saint Mary's College of California
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Privacy Notice */}
                  <div className="notification is-info is-light mt-5">
                    <p className="is-size-7">
                      <strong>Privacy Notice:</strong> Your information will be kept secure and will 
                      only be used to facilitate your participation in the Navigating Next program. 
                      We will not share your information with third parties without your consent.
                    </p>
                  </div>

                  {/* Submit Button */}
                  <div className="field mt-5">
                    <div className="control">
                      <button 
                        className={`button is-primary is-fullwidth is-medium ${loading ? 'is-loading' : ''}`}
                        type="submit"
                        disabled={loading}
                      >
                        Sign Up
                      </button>
                    </div>
                  </div>

                  <p className="has-text-centered mt-4 is-size-7 has-text-grey">
                    * Required fields
                  </p>
                </form>
              </div>

              {/* Footer */}
              <div className="has-text-centered mt-4">
                <p className="is-size-7 has-text-grey">
                  Questions? Contact us at{' '}
                  <a href="mailto:support@navigatingnext.org">support@navigatingnext.org</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PublicOptIn;